import { cn } from "@/lib/utils";
import ChipOutlined from "@/toggle-ui-kit/chip/chip-outlined";
import ChipSolid from "@/toggle-ui-kit/chip/chip-solid";

// ============================================
// 타입 정의 (DTO 기반)
// ============================================

export type ReportPeriod = "Q1" | "Q2" | "Q3" | "Q4";
export type ReportType = "CONSOLIDATED" | "SEPARATE";

type ChipSize = "small" | "medium" | "large";

/** Figma 실사용 스펙(22px/13px) 기준 매핑 — toggle-ui-kit/chip의 사이즈 체계로 변환 */
const chipSizeMap: Record<ChipSize, "18px" | "20px" | "22px"> = {
  small: "18px",
  medium: "20px",
  large: "22px",
};

// ============================================
// DTO → 표시 텍스트 매핑
// ============================================

const periodLabels: Record<ReportPeriod, string> = {
  Q1: "1분기",
  Q2: "반기",
  Q3: "3분기",
  Q4: "사업",
};

const typeLabels: Record<ReportType, string> = {
  CONSOLIDATED: "연결",
  SEPARATE: "별도",
};

// 라벨 → enum 역방향. 위 표를 뒤집어 만들어 둘이 어긋날 수 없게 한다.
const periodByLabel = Object.fromEntries(
  Object.entries(periodLabels).map(([enumValue, label]) => [label, enumValue]),
) as Record<string, ReportPeriod>;

const typeByLabel = Object.fromEntries(
  Object.entries(typeLabels).map(([enumValue, label]) => [label, enumValue]),
) as Record<string, ReportType>;

/**
 * 서버가 준 기간 라벨("사업"·"1분기"…) → Chip 의 ReportPeriod.
 * 모르는 값이면 undefined — 호출부는 칩을 빼서 추측값을 단언하지 않는다.
 * (기간·유형은 사용자가 올린 DSD 에서 읽은 값이라 빈 문자열이나 낯선 값이 올 수 있다)
 */
export const toReportPeriod = (
  period: string | null,
): ReportPeriod | undefined => periodByLabel[period ?? ""];

/** 보고서 유형 라벨("연결"·"별도") → Chip 의 ReportType. 모르는 값은 undefined. */
export const toReportType = (
  reportType: string | null,
): ReportType | undefined => typeByLabel[reportType ?? ""];

// ============================================
// 피그마 디자인 기반 색상 설정
// ============================================

/**
 * Figma 의 stroke 는 안쪽으로 그려져 칩 크기를 늘리지 않는다. CSS border 는 box 를 2px
 * 키우므로(box-sizing: border-box + 고정 높이) 테두리는 inset ring 으로 그린다.
 */
const OUTLINED_RING = "border-0 ring-1 ring-inset ring-line-normal";

const periodStyles: Record<ReportPeriod, string> = {
  Q1: `bg-white text-label-strong ${OUTLINED_RING}`,
  Q2: `bg-white text-label-strong ${OUTLINED_RING}`,
  Q3: `bg-white text-label-strong ${OUTLINED_RING}`,
  Q4: `bg-white text-label-strong ${OUTLINED_RING}`,
};

const typeStyles: Record<ReportType, string> = {
  CONSOLIDATED: "bg-primary-base text-static-white",
  SEPARATE: "bg-primary-color text-static-white",
};

// ============================================
// PeriodChip 컴포넌트
// ============================================

interface PeriodChipProps {
  period: ReportPeriod;
  size?: ChipSize;
  className?: string;
}

/**
 * 보고서 기간 칩 컴포넌트
 * - Q1(1분기): indigo, Q2(반기): sky, Q3(3분기): amber, Q4(사업): fuchsia
 */
export const PeriodChip = ({
  period,
  size = "large",
  className,
}: PeriodChipProps) => (
  <ChipOutlined
    size={chipSizeMap[size]}
    rounded={false}
    showRightIcon={false}
    className={cn(periodStyles[period], className)}
  >
    {periodLabels[period]}
  </ChipOutlined>
);

// ============================================
// TypeChip 컴포넌트
// ============================================

interface TypeChipProps {
  type: ReportType;
  size?: ChipSize;
  className?: string;
}

/**
 * 보고서 유형 칩 컴포넌트
 * - CONSOLIDATED(연결): stone, SEPARATE(별도): slate
 */
export const TypeChip = ({
  type,
  size = "large",
  className,
}: TypeChipProps) => (
  <ChipSolid
    size={chipSizeMap[size]}
    rounded={false}
    showRightIcon={false}
    className={cn(typeStyles[type], className)}
  >
    {typeLabels[type]}
  </ChipSolid>
);

// ============================================
// YearChip 컴포넌트
// ============================================

interface YearChipProps {
  year: string | number;
  size?: ChipSize;
  className?: string;
}

/**
 * 보고서 연도 칩 컴포넌트
 * - slate 배경 + 검정 텍스트
 */
export const YearChip = ({
  year,
  size = "large",
  className,
}: YearChipProps) => (
  <ChipOutlined
    size={chipSizeMap[size]}
    rounded={false}
    showRightIcon={false}
    className={cn(`bg-white text-label-strong ${OUTLINED_RING}`, className)}
  >
    {typeof year === "number" ? `${year}년도` : year}
  </ChipOutlined>
);

export const getTypeLabel = (type: ReportType): string => typeLabels[type];
